import { LitElement, html, css, nothing, svg } from "@lit";
import { t } from "@nova/utils";

/**
 * @typedef {Object} SeamGuidelinesProperties
 * @property {number} wInch - Width value in inches
 * @property {number} lInch - Length value in inches
 * @property {'impertial' | 'metric' } unit - Unit type in `imperial` or `metric` for displaying values
 * @property {number} seamIntervalInch - Interval value for between seams in inches
 */
class SeamGuildelinesPreview extends LitElement {
  /**
   * @type {SeamGuidelinesProperties}
   */
  static properties = {
    wInch: { type: Number, attribute: "width-inch", reflect: true },
    lInch: { type: Number, attribute: "length-inch", reflect: true },
    unit: { type: String, reflect: true },
    seamIntervalInch: { type: Number, attribute: "seam-interval-inch" },
  };

  connectedCallback() {
    this.unit = this.unit || "imperial";
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  resetValues() {
    this.wInch = "";
    this.lInch = "";
  }

  #convertInchesToFeetAndInches(totalInches) {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return { feet, inches };
  }

  #inchesToMeters(inches) {
    return inches * 0.0254;
  }

  #safeCeil(num, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.ceil(num * factor + Number.EPSILON) / factor;
  }

  #displayUnits(size) {
    if (this.unit == "metric") {
      const meter = this.#inchesToMeters(size);
      return `${Math.floor(meter * 100) / 100} m`;
    } else {
      const { feet, inches } = this.#convertInchesToFeetAndInches(size);

      if (inches == 0) {
        return `${feet} ft`;
      } else {
        return `${feet} ft ${inches} in`;
      }
    }
  }

  get seamlines() {
    if (!this.wInch || !this.lInch) return nothing;

    const seamCount = Math.max(
      Math.floor((this.wInch - 1) / this.seamIntervalInch),
      0,
    );

    return svg`
     ${[...Array(seamCount)].map((_, index) => {
       const distance = (100 / (seamCount + 1)) * (index + 1);

       if (distance == 100) return nothing;
       return svg`
        <line
          x1="0"
          y1="${distance}%"
          x2="100%"
          y2="${distance}%"
          stroke-dasharray="12 24"
          stroke="currentColor"
          stroke-width="1"
          marker-start="url(#seam-guideline-arrow-end)"
          marker-end="url(#seam-guideline-arrow-end)"
        />
      `;
     })}
    `;
  }

  get aspectRatio() {
    if (!this.wInch || !this.lInch) return;

    return css`
      --aspect-ratio: ${this.lInch} / ${this.wInch};
    `;
  }

  render() {
    if (!this.wInch || !this.lInch) return nothing;

    return html`
      <div class="seam-guideline-preview__wrapper">
        <div class="seam-guideline-preview__legend">
          <svg
            width="45"
            height="2"
            viewBox="0 0 45 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 1L1 1"
              stroke="#A5A7A8"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M44 1L28 1"
              stroke="#A5A7A8"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <span>${t("products.seam_guidelines.preview.seam_lines")}</span>
        </div>

        <div
          class="seam-guideline-preview__content"
          style="${this.aspectRatio}"
        >
          <svg
            class="seam-guideline-preview__svg"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <marker
                id="seam-guideline-arrow-end"
                viewBox="0 0 3 3"
                refX="3"
                refY="1.5"
                markerWidth="3"
                markerHeight="3"
                orient="auto-start-reverse"
              >
                <path
                  d="M1.78873e-08 1.5L3 -2.02644e-07L3 3L1.78873e-08 1.5Z"
                  fill="currentColor"
                />
              </marker>
            </defs>
            <rect height="100%" width="100%" fill="var(--background-color)" />
            ${this.seamlines}
          </svg>

          <div
            class="seam-guideline-preview__distance seam-guideline-preview__distance--width"
          >
            <span class="seam-guideline-preview__distance-unit"
              >${this.#displayUnits(this.wInch)}</span
            >
          </div>
          <div
            class="seam-guideline-preview__distance seam-guideline-preview__distance--length"
          >
            <span class="seam-guideline-preview__distance-unit"
              >${this.#displayUnits(this.lInch)}</span
            >
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`
    .seam-guideline-preview__wrapper {
      --background-color: var(
        --seam-guideline-preview-background-color,
        #f5f5f5
      );
      color: var(--seam-guideline-preview-color, #686c6d);
    }

    .seam-guideline-preview__legend {
      display: flex;
      gap: 10px;
      align-items: center;
      margin-bottom: 19px;

      @media (min-width: 768px) {
        margin-bottom: 16px;
      }
    }

    .seam-guideline-preview__content {
      display: grid;
      gap: 1rem;
      grid-template-columns: auto 1rem;
      grid-template-rows: auto 1rem;
    }

    .seam-guideline-preview__svg {
      aspect-ratio: var(--aspect-ratio, 16 / 9);
      width: 100%;
      height: auto;
    }

    .seam-guideline-preview__distance {
      --background-color: #d9d9d9;
      position: relative;
      display: flex;
      justify-content: center;
      font: var(--nova-body-utility);
      letter-spacing: var(--nova-utility-letter-spacing);
      text-transform: var(--nova-utility-text-transform);

      &::before,
      &::after {
        content: "";
        position: absolute;
        inset-block-start: calc(50% - 5px);
        block-size: 5px;
        inline-size: 1px;
        background-color: var(--background-color);
      }

      &::before {
        inset-inline-start: 0;
      }

      &::after {
        inset-inline-end: 0;
      }
    }

    .seam-guideline-preview__distance-unit {
      &::before,
      &::after {
        content: "";
        position: absolute;
        inset-block-start: 50%;
        inset-inline: 0 0;
        block-size: 1px;
        background-color: var(--background-color);
        z-index: -1;
      }

      &::before {
        inset-inline-start: 0;
        inset-inline-end: calc(50% + 2.5rem);
      }

      &::after {
        inset-inline-start: calc(50% + 2.5rem);
        inset-inline-end: 0;
      }
    }

    .seam-guideline-preview__distance--width {
      writing-mode: tb-rl;
      transform: rotate(-180deg);
    }
  `;
}

if (typeof customElements.get("seam-guidelines-preview") === "undefined") {
  customElements.define("seam-guidelines-preview", SeamGuildelinesPreview);
}

class SeamGuidelinesDialogPreviewWrapper extends LitElement {
  constructor() {
    super();
  }

  static properties = {
    _inputWidth: {
      type: Number,
      attribute: "width",
      reflect: true,
    },
    _inputLength: {
      type: Number,
      attribute: "length",
      reflect: true,
    },
    isCustom: {
      type: Boolean,
      attribute: 'is-custom'
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.dialogPreview = this.querySelector("seam-guidelines-preview");

    if (!this.dialogPreview) return;

    if(!this.isCustom) return;

    this.customCalculatorValueEl = document.querySelector(
      "nova-custom-calculator-values",
    );

    this.unitSelector = document.querySelector(".js-custom-size-unit-type");
    this.unitSelector?.addEventListener(
      "sl-change",
      this.updateUnitType.bind(this),
    );

    this.customCalculatorValueEl?.addEventListener(
      "custom-calculator:values-updated",
      this.updatePreview.bind(this),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if(!this.isCustom) return;

    this.unitSelector?.removeEventListener(
      "sl-change",
      this.updateUnitType.bind(this),
    );

    this.customCalculatorValueEl?.removeEventListener(
      "custom-calculator:values-updated",
      this.updatePreview.bind(this),
    );
  }

  updatePreview(event) {
    if (!event.detail) return;
    const { value, type } = event?.detail;

    if (type == "w") {
      this._inputWidth = value;
    } else if (type == "h") {
      this._inputLength = value;
    }
  }

  updateUnitType(event) {
    const value = event.target.value;
    this.dialogPreview.setAttribute("unit", value);
    this._inputWidth = 0;
    this._inputLength = 0;
  }

  willUpdate(changedProperties) {
    if (changedProperties.has("_inputWidth") || changedProperties.has("_inputLength")) {
      this.dialogPreview.setAttribute("width-inch", this._inputWidth);
      this.dialogPreview.setAttribute("length-inch", this._inputLength);
    } 
  }

  createRenderRoot() {
    return this;
  }
}

if (
  typeof customElements.get("seam-guidelines-dialog-preview-wrapper") ===
  "undefined"
) {
  customElements.define(
    "seam-guidelines-dialog-preview-wrapper",
    SeamGuidelinesDialogPreviewWrapper,
  );
}
